"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Benefits 1063 — KUK "Why Choose Us?".
 *
 * Port of kuk-campus-portal `components/Degree/KeyBenefits.tsx`: tall shadowed cards,
 * each an icon over a title and a line of copy, as a horizontal rail from 768px up
 * and a two-column grid below it, with a "Get in touch" CTA beside the heading on
 * desktop and at the foot on mobile.
 *
 * WHY IT IS NOT PROGRAM BENEFITS 5008. 5008 ports `components/Home/KeyBenefits.tsx`,
 * a different component that shares only its name: a wrapping grid of photographs
 * washed out behind the icon. This one has no imagery at all — flat cards, a scroll
 * rail and arrow buttons — so the two are separate variants, as they are upstream.
 *
 * THE ARROWS ARE STATEFUL, not decorative. v1 measures the rail on every scroll and
 * shows the left arrow only once scrolled and the right only while there is more to
 * reach; that is reproduced against the real DOM node, and re-measured on resize and
 * whenever the row set changes, so a CMS edit cannot strand a stale arrow.
 *
 * TWO PRESENTATIONS, ONE SET OF ROWS. From `tab` up the cards are a rail; below it
 * they become a two-column grid. That is v1's behaviour, and both read the same
 * `metadata` rows.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * DROPPED — v1's `custom-underline` class on the heading, a project-level rule that
 * draws a brand-coloured bar under it. This app has no equivalent global, so the rule
 * is expressed as a real CMS-coloured border instead of importing a stylesheet for
 * one selector — the same treatment Program Benefits 5009 gives it.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch, which swaps the CTA for a
 * "Continue Application" link to the admission portal. That is per-visitor session
 * state, not page content.
 */

/** v1: `width="43"` on the rail card icon, `"32"` on the grid card. */
const ICON_SIZE_RAIL = "43";
const ICON_SIZE_GRID = "32";

/** v1's `.custom-underline` — see Program Benefits 5009 for the full note. */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

/** v1 scrolls the rail by 200px per arrow press. */
const SCROLL_STEP = 200;

interface BenefitRow {
  id?: string | number;
  /** An `iconMap` key (e.g. "tutor") or an uploaded icon URL. */
  meta_icon?: string;
  meta_title?: string;
  meta_sub_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits1063Data {
  status?: boolean | number | string;
  title?: string;
  title_start_color?: string;
  /** The rule under the heading. */
  title_end_color?: string;
  desktop_background_color?: string;
  /** Card fill. */
  card_hover_background_color?: string;
  /** Card icon tint. */
  gradient_color_1?: string;
  /** Card title / body. */
  subtitle_color?: string;
  description_color?: string;
  /**
   * CTA. `program_benefits` has no URL column, and the reference's button only ever
   * opens the lead form, so this is deliberately not linkable.
   */
  button_text?: string;
  button_color?: string;
  button_text_color?: string;
  button_icon_url?: string;
  metadata?: BenefitRow[];
}

const ProgramBenefits1063 = (props: { data?: ProgramBenefits1063Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramBenefits1063Data
    | undefined;
  const dispatch = useDispatch();

  const {
    title = "",
    title_start_color = "#101112",
    title_end_color = "#9F1C33",
    desktop_background_color = "#FFFFFF",
    card_hover_background_color = "#FFFFFF",
    gradient_color_1 = "#9F1C33",
    subtitle_color = "#101112",
    description_color = "#4B4B4B",
    button_text = "Get in touch",
    button_color = "#9F1C33",
    button_text_color = "#FFFFFF",
    button_icon_url = "",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [] as BenefitRow[];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_sub_title))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const railRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const measure = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeft(scrollLeft > 0);
    setShowRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    measure();
    el.addEventListener("scroll", measure);
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
    // Re-measure when the row set changes so a CMS edit cannot strand an arrow.
  }, [measure, cards.length]);

  const scroll = useCallback((direction: "left" | "right") => {
    railRef.current?.scrollBy({
      left: direction === "left" ? -SCROLL_STEP : SCROLL_STEP,
      behavior: "smooth",
    });
  }, []);

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "program_benefits_1063",
      component: "why_choose_us",
      label: button_text?.trim() || "get_in_touch",
      step: "1",
      entity: "program_benefits_1063",
      parentEntity: "program_benefits_1063",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [button_text, dispatch]);

  if (!status || cards.length === 0) return null;

  const ctaStyle = { backgroundColor: button_color, color: button_text_color };

  const renderCta = (className: string) => {
    if (!button_text) return null;
    return (
      <button
        type="button"
        onClick={handleCtaClick}
        className={cn(
          "h-[37px] items-center justify-center gap-2 whitespace-nowrap",
          "rounded-md px-6 text-sm font-light shadow transition-colors",
          className,
        )}
        style={ctaStyle}
      >
        {button_icon_url ? (
          <DynamicIcon
            iconName={button_icon_url}
            width="18"
            height="18"
            color={button_text_color}
          />
        ) : null}
        <span>{button_text}</span>
      </button>
    );
  };

  const cardIcon = (row: BenefitRow, size: string) =>
    row.meta_icon ? (
      <DynamicIcon
        iconName={row.meta_icon}
        width={size}
        height={size}
        color={gradient_color_1}
      />
    ) : null;

  const cardBody = (row: BenefitRow) => (
    <>
      {row.meta_title ? (
        <h3
          className="text-lg font-bold leading-5"
          style={{ color: subtitle_color }}
        >
          {row.meta_title}
        </h3>
      ) : null}
      {row.meta_sub_title ? (
        <p className="text-sm font-light" style={{ color: description_color }}>
          {row.meta_sub_title}
        </p>
      ) : null}
    </>
  );

  return (
    <section
      className="w-full p-5 pb-10"
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="mx-auto w-full max-w-[1120px]">
        <div className="flex items-center justify-start gap-5 px-5 pt-10 tab:px-20">
          {title ? (
            <h2
              className="relative w-fit text-2xl font-bold tab:text-4xl"
              style={{ color: title_start_color }}
            >
              {title}
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: title_end_color }}
              />
            </h2>
          ) : null}
          {/* Desktop: beside the heading, as in the reference. */}
          {renderCta("hidden tab:inline-flex")}
        </div>

        {/* ── tab and up: horizontal rail with measured arrows ─────────────── */}
        <div className="relative hidden overflow-visible pb-5 tab:block">
          <div
            ref={railRef}
            className="scrollbar-hide relative flex gap-4 overflow-x-auto py-10"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {cards.map((row, index) => (
              <div
                key={row.id ?? index}
                className={cn(
                  "flex min-h-[360px] w-[240px] min-w-[240px] flex-col items-start",
                  "justify-start gap-7 rounded-md p-5 pb-6 pt-10 shadow-xl",
                )}
                style={{ backgroundColor: card_hover_background_color }}
              >
                {cardIcon(row, ICON_SIZE_RAIL)}
                {cardBody(row)}
              </div>
            ))}
          </div>
          {showLeft ? (
            <button
              type="button"
              onClick={() => scroll("left")}
              aria-label="Scroll left"
              className={cn(
                "absolute -left-6 top-1/2 z-20 size-10 -translate-y-1/2",
                "rounded-full bg-[#FFFFFF] p-2 shadow-md",
              )}
            >
              &#8592;
            </button>
          ) : null}
          {showRight ? (
            <button
              type="button"
              onClick={() => scroll("right")}
              aria-label="Scroll right"
              className={cn(
                "absolute -right-6 top-1/2 z-20 size-10 -translate-y-1/2",
                "rounded-full bg-[#FFFFFF] p-2 shadow-md",
              )}
            >
              &#8594;
            </button>
          ) : null}
        </div>

        {/* ── below tab: two-column grid ───────────────────────────────────── */}
        <div className="grid w-full grid-cols-2 gap-2 px-5 py-10 tab:hidden">
          {cards.map((row, index) => (
            <div
              key={row.id ?? index}
              className={cn(
                "flex min-h-[258px] min-w-[155px] flex-col items-start",
                "justify-start gap-7 p-5 shadow-xl",
              )}
              style={{ backgroundColor: card_hover_background_color }}
            >
              {cardIcon(row, ICON_SIZE_GRID)}
              {cardBody(row)}
            </div>
          ))}
        </div>

        {/* Mobile: full-width at the foot, as in the reference. */}
        <div className="flex w-full justify-center tab:hidden">
          {renderCta("flex w-[90%]")}
        </div>
      </div>
    </section>
  );
};

export default ProgramBenefits1063;
